import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../api/axiosInstance";
import { delay } from "../helper/delayresponse";

interface AllowIp {
    id?: number;
    ip?: string;
    status?: number;
    createdAt?: string;
    updatedAt?: string;
}

interface AllowIpaddressState {
    allowipdata: AllowIp[];
    info: { count: number; current_page: number; limit: number; total_page: number } | null;
    error: string | null;
    loading: boolean;
}

//==================get-api-action-start============= 
export const GetAllipAddress = createAsyncThunk<
    { data: AllowIp[], info: { count: number; current_page: number; limit: number; total_page: number } },
    { search: string; page: number; limit: number },
    { rejectValue: string }
>(
    "GetAllipAddress",
    async ({ search, page, limit }, { rejectWithValue }) => {
        try {
            await delay(1000);
            const response = await axiosInstance.get(`/api/admin/ip-address`, {
                params: { search, page, limit },
            });
            return { data: response.data.data, info: response.data.info };
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response.data.message || 'Request failed');
            }
            return rejectWithValue(error.message || 'Unknown error');
        }
    }
);
//==================get-api-action-end=============

//==================post-api-action-start=============
export const AddIpAddress = createAsyncThunk<
    AllowIp,
    AllowIp,
    { rejectValue: string }
>(
    'allowipdata/AddIpAddress',
    async (allowipData, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.post(`/api/admin/ip-address/create`, allowipData);
            return response.data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response?.data?.message);
            }
            return rejectWithValue(error.message);
        }
    }
);
//==================post-api-action-end=============


// =================Updateagentmonitors-action-start=========
export const UpdateIpAddress = createAsyncThunk<
    AllowIp,
    { id: number; allowipData: AllowIp },
    { rejectValue: string }
>(
    'allowipdata/UpdateIpAddress',
    async ({ id, allowipData }, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.put(`/api/admin/ip-address/update/${id}`, allowipData);
            return response.data;
        }
        catch (error: any) {
            if (error.response && error.response.data) {
                return rejectWithValue(error.response?.data?.message);
            }
            return rejectWithValue(error.message);
        }
    }
);
// =================Updateagentmonitors-action-end=========


// =================Delete-api-action-start===========
export const DeleteIpAddress = createAsyncThunk<
    { id: number },
    { id: number }
>(
    'allowipdata/DeleteIpAddress',
    async ({ id }) => {
        try {
            const { data } = await axiosInstance.delete(`/api/admin/ip-address/${id}`);
            return data;
        } catch (error: any) {
            if (error.response && error.response.data) {
                return error.response.data;
            }
            return error.message;
        }
    }
);
// =================Delete-api-action-end===========



const initialState: AllowIpaddressState = {
    allowipdata: [],
    info: null,
    error: null,
    loading: false,
};

const allowIpSlice = createSlice({
    name: "allowIp",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // ==========get-api-addcase-start==========
            .addCase(GetAllipAddress.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(GetAllipAddress.fulfilled, (state, action: PayloadAction<{ data: AllowIp[], info: { count: number; current_page: number; limit: number; total_page: number } }>) => {
                state.loading = false;
                state.allowipdata = action.payload.data;
                state.info = action.payload.info;
            })
            .addCase(GetAllipAddress.rejected, (state, action: PayloadAction<string | undefined>) => {
                state.loading = false;
                state.error = action.payload || 'An error occurred';
            });
        // ==========get-api-addcase-end==========

        // ==========post-api-addcase-start==========
        builder
            .addCase(AddIpAddress.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(AddIpAddress.fulfilled, (state, action: PayloadAction<AllowIp>) => {
                state.loading = false;
                state.allowipdata.push(action.payload);
            })
            .addCase(
                AddIpAddress.rejected, (state, action: PayloadAction<string | undefined>) => {
                    state.loading = false;
                    state.error = action.payload || 'Failed to add Allow IP';
                }
            );
        // ==========post-api-addcase-end==========

        // =================Update agent/monitor-slice-start============
        builder
            .addCase(UpdateIpAddress.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(UpdateIpAddress.fulfilled, (state, action: PayloadAction<AllowIp>) => {
                state.loading = false;
                const index = state.allowipdata.findIndex(item => item.id === action.payload.id);
                if (index !== -1) {
                    state.allowipdata[index] = action.payload;
                }
            })
            .addCase(UpdateIpAddress.rejected, (state, action: PayloadAction<string | undefined>) => {
                state.loading = false;
                state.error = action.payload || 'Failed to update Allow IP';
            });
        // =================Update agent/monitor-slice-start============

        // ================Delete-api-start=========
        builder
            .addCase(DeleteIpAddress.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(DeleteIpAddress.fulfilled, (state, action: PayloadAction<{ id: number }>) => {
                state.loading = false;
                state.allowipdata = state.allowipdata.filter(item => item.id !== action.payload.id);
            })
            .addCase(DeleteIpAddress.rejected, (state, action: any) => {
                state.loading = false;
                state.error = action.payload || 'Failed to delete allow IP';
            });
        // ================Delete-api-end=========
    },
});

export default allowIpSlice.reducer;
